/**
 * PATH: packages/document-editor/src/DocumentToolbar.tsx
 * FILE_ID: DOCEDITOR-TOOLBAR-001
 * PURPOSE: Generic formatting controls for the shared document schema.
 * WHY: Domain packages add actions around one predictable toolbar contract.
 * RELATED: PaginatedPageShell.tsx, extensions.ts
 */
import { useEffect, useReducer } from 'react'
import type { Editor } from '@tiptap/core'
import {
  AlignCenter, AlignLeft, AlignRight, Bold, Columns3, FileDown, FileText,
  Highlighter, Image, Italic, Link, List, ListOrdered, Redo2, Rows3, Table,
  Trash2, Underline, Undo2,
} from 'lucide-react'
import type { FileRecordReference } from './contracts'

export interface DocumentToolbarProps {
  editor: Editor | null
  onSelectImage?: () => Promise<FileRecordReference | null>
  domainActions?: React.ReactNode
}

function useEditorRevision(editor: Editor | null) {
  const [, update] = useReducer((value: number) => value + 1, 0)
  useEffect(() => {
    if (!editor) return
    const listener = () => update()
    editor.on('selectionUpdate', listener)
    editor.on('transaction', listener)
    return () => {
      editor.off('selectionUpdate', listener)
      editor.off('transaction', listener)
    }
  }, [editor])
}

export function DocumentToolbar({
  editor,
  onSelectImage,
  domainActions,
}: DocumentToolbarProps) {
  useEditorRevision(editor)
  if (!editor) return null
  const insertImage = async () => {
    const image = await onSelectImage?.()
    if (!image) return
    editor.chain().focus().insertContent({
      type: 'image',
      attrs: { fileId: image.fileId, src: image.url ?? '', url: image.url, alt: image.name ?? '' },
    }).run()
  }
  const insertLink = () => {
    const href = window.prompt('Link URL', String(editor.getAttributes('link').href ?? 'https://'))
    if (!href) return
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
  }
  const hasImage = editor.extensionManager.extensions.some(({ name }) => name === 'image')
  const hasTable = editor.extensionManager.extensions.some(({ name }) => name === 'table')
  const hasReportNodes = editor.extensionManager.extensions.some(({ name }) => name === 'pageBreak')
  const imageSelected = editor.isActive('image')
  const tableSelected = editor.isActive('table')
  const insertGovernedBlock = (type: 'headerBlock' | 'footerBlock') => {
    editor.chain().focus().insertContent({
      type,
      attrs: { scope: 'document', sectionId: '' },
      content: [{ type: 'paragraph' }],
    }).run()
  }
  return (
    <div className="qdoc-toolbar flex flex-wrap items-center gap-1 border-b border-slate-200 p-2" role="toolbar" aria-label="Document formatting">
      <select
        aria-label="Text style"
        value={editor.isActive('heading', { level: 1 }) ? 'h1' : editor.isActive('heading', { level: 2 }) ? 'h2' : 'p'}
        onChange={(event) => {
          const value = event.target.value
          if (value === 'p') editor.chain().focus().setParagraph().run()
          else editor.chain().focus().setHeading({ level: Number(value.slice(1)) as 1 | 2 | 3 | 4 }).run()
        }}
      >
        <option value="p">Paragraph</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
        <option value="h4">Heading 4</option>
      </select>
      <ToolButton label="Bold" active={editor.isActive('bold')} icon={Bold} onClick={() => editor.chain().focus().toggleBold().run()} />
      <ToolButton label="Italic" active={editor.isActive('italic')} icon={Italic} onClick={() => editor.chain().focus().toggleItalic().run()} />
      <ToolButton label="Underline" active={editor.isActive('underline')} icon={Underline} onClick={() => editor.chain().focus().toggleUnderline().run()} />
      <ToolButton label="Bullet list" active={editor.isActive('bulletList')} icon={List} onClick={() => editor.chain().focus().toggleBulletList().run()} />
      <ToolButton label="Numbered list" active={editor.isActive('orderedList')} icon={ListOrdered} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
      <ToolButton label="Link" active={editor.isActive('link')} icon={Link} onClick={insertLink} />
      <label className="flex items-center gap-1 text-xs">
        Text <input aria-label="Text colour" type="color" onChange={(event) => editor.chain().focus().setColor(event.target.value).run()} />
      </label>
      <ToolButton label="Highlight" active={editor.isActive('highlight')} icon={Highlighter} onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()} />
      {hasReportNodes && <ToolButton label="Insert page break" icon={FileDown} onClick={() => editor.chain().focus().insertContent({ type: 'pageBreak' }).run()} />}
      {hasReportNodes && <ToolButton label="Insert header" icon={FileText} onClick={() => insertGovernedBlock('headerBlock')} />}
      {hasReportNodes && <ToolButton label="Insert footer" icon={FileText} onClick={() => insertGovernedBlock('footerBlock')} />}
      {hasImage && onSelectImage && <ToolButton label="Insert image" icon={Image} onClick={() => void insertImage()} />}
      {imageSelected && (
        <div className="qdoc-context-controls" aria-label="Selected image controls">
          <select
            aria-label="Image width"
            value={String(editor.getAttributes('image').width ?? '100%')}
            onChange={(event) => editor.chain().focus().updateAttributes('image', { width: event.target.value }).run()}
          >
            <option value="25%">25%</option>
            <option value="50%">50%</option>
            <option value="75%">75%</option>
            <option value="100%">100%</option>
          </select>
          <ToolButton label="Align image left" icon={AlignLeft} onClick={() => editor.chain().focus().updateAttributes('image', { align: 'left' }).run()} />
          <ToolButton label="Align image centre" icon={AlignCenter} onClick={() => editor.chain().focus().updateAttributes('image', { align: 'center' }).run()} />
          <ToolButton label="Align image right" icon={AlignRight} onClick={() => editor.chain().focus().updateAttributes('image', { align: 'right' }).run()} />
        </div>
      )}
      {hasTable && <ToolButton label="Insert table" icon={Table} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} />}
      {tableSelected && (
        <div className="qdoc-context-controls" aria-label="Selected table controls">
          <ToolButton label="Add row" icon={Rows3} onClick={() => editor.chain().focus().addRowAfter().run()} />
          <ToolButton label="Delete row" icon={Rows3} onClick={() => editor.chain().focus().deleteRow().run()} />
          <ToolButton label="Add column" icon={Columns3} onClick={() => editor.chain().focus().addColumnAfter().run()} />
          <ToolButton label="Delete column" icon={Columns3} onClick={() => editor.chain().focus().deleteColumn().run()} />
          <ToolButton label="Delete table" icon={Trash2} onClick={() => editor.chain().focus().deleteTable().run()} />
        </div>
      )}
      <ToolButton label="Undo" icon={Undo2} onClick={() => editor.chain().focus().undo().run()} />
      <ToolButton label="Redo" icon={Redo2} onClick={() => editor.chain().focus().redo().run()} />
      {domainActions}
    </div>
  )
}

function ToolButton({
  label,
  icon: Icon,
  onClick,
  active = false,
}: {
  label: string
  icon: React.ComponentType<{ className?: string }>
  onClick: () => void
  active?: boolean
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      <Icon className="h-4 w-4" />
    </button>
  )
}
